import { CheckIcon } from '@chakra-ui/icons';
import { Box, Button, Flex, Grid, Skeleton, Stack, Text, useBoolean, useColorModeValue, useToast } from '@chakra-ui/react';
import NiceModal from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import { QueryKey, useMutation, useQueryClient } from '@tanstack/react-query';
import { Account } from 'api/types';
import AddonIconButton from 'components/form-elements/AddonIconButton';
import { TotpCodeDisplay } from 'components/TotpCodeDisplay';
import { simpleCheckBox, simpleInput } from 'helpers/makeFormFields';
import { manageErrorResponse } from 'helpers/manageErrorResponse';
import { map, property } from 'lodash';
import { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { IoIosCopy } from 'react-icons/io';
import { MdOpenInNew } from 'react-icons/md';
import { useCopyToClipboard } from 'usehooks-ts';
import * as yup from 'yup';

import Link from '../../components/Link';
import AlertModal from '../../components/modals/AlertModal';
import { StatusTag } from '../../components/StatusTag';
import useUpdateAccountStatusMutation from '../../hooks/mutations/useUpdateAccountStatusMutation';

const CredentialCard = (props: {
  serviceRecordStatus?: number;
  sendRequest(x: object): Promise<void>;
  serviceRecordStatusReason?: number;
  credential: Account | undefined;
  isLoading: boolean;
  queryKey?: QueryKey;
  serviceRecordIdbs?: boolean;
  serviceRecordAccountId?: number;
}) => {
  const { t } = useTranslation();
  const { isLoading, credential, serviceRecordStatus, serviceRecordStatusReason, queryKey, sendRequest, serviceRecordIdbs, serviceRecordAccountId } =
    props;
  const queryClient = useQueryClient();
  const [value, copy] = useCopyToClipboard();
  const [showCheckIcon, setShowCheckIcon] = useBoolean(false);
  const [canEdit, setCanEdit] = useState(false);
  const toast = useToast();
  const account_id = credential?.id as number;
  const updateAccountStatusMutation = useUpdateAccountStatusMutation(account_id);
  const SHOW_CHECKICON_DURATION = 700;

  const schema = yup
    .object({
      username: yup.string().required(),
      password: yup
        .string()
        .nullable()
        .when('random_password', {
          is: false,
          then: (schema) => schema.required(),
        }),
      random_password: yup.boolean(),
      status: yup.number().required(),
    })
    .required();

  const {
    handleSubmit,
    formState: { errors, isValid, submitCount },
    register,
    setValue,
    setError,
    getValues,
    watch,
    reset,
  } = useForm<any>({
    resolver: yupResolver(schema),
    defaultValues: {
      status: credential?.status,
      //@ts-ignore
      portal_id: credential?.portal?.id,
      workshop_id: credential?.workshop_id,
      username: credential?.username,
      password: credential?.password,
      random_password: false,
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const randomPassword = watch('random_password');

  useEffect(() => {
    reset({
      status: credential?.status,
      //@ts-ignore
      portal_id: credential?.portal?.id,
      workshop_id: credential?.workshop_id,
      username: credential?.username,
      password: credential?.password,
      random_password: randomPassword,
    });
  }, [credential, randomPassword, reset]);

  const { mutate, isPending: isLoadingForm } = useMutation<any, Error, any>({
    mutationFn: async (data) => {
      // @ts-ignore
      if (credential?.status > 0 && !data?.force) {
        NiceModal.show(AlertModal as any, {
          onSubmit: () => mutate({ ...getValues(), force: true }),
          children: t('workshop:credentials.modal.description', {
            make: map((credential?.portal as any)?.makes, property('name')).join(' / '),
          }),
          onCancel: () => null,
          content: {
            header: t('workshop:credentials.modal.header', {
              make: map((credential?.portal as any)?.makes, property('name')).join(' / '),
            }),
            footer: {
              buttons: {
                cancelCaption: t('common:abort'),
                actionCaption: t('workshop:credentials.modal.submit'),
              },
            },
          },
        });
        return;
      } else {
        setValue('random_password', false);
        return sendRequest(data).then(() => setCanEdit(false));
      }
    },
  });

  const onSubmit: SubmitHandler<any> = (data) => {
    mutate(data, {
      onError: (error: any) => {
        manageErrorResponse(toast, error.response);
        const { errors } = error.response.data;

        (Object.keys(errors) as Array<keyof any>).forEach((key) => {
          setError(String(key), {
            type: 'server',
            message: errors[key]!.join('. '),
          });
        });
      },
    });
  };

  const shadow = useColorModeValue('lg', 'lg-dark');

  if (isLoading) {
    return <Skeleton height="200px" speed={3} fadeDuration={4} />;
  }

  return (
    <Grid
      opacity={isLoadingForm ? 0.5 : 1}
      onSubmit={handleSubmit(onSubmit)}
      gap={4}
      bgColor={credential?.status === 200 ? 'green.200' : 'orange.200'}
      boxShadow={shadow}
      borderRadius="lg"
      position="relative"
      py={{ base: '2', md: '4' }}
      px={{ base: '2', md: '4' }}
      as="form"
    >
      {serviceRecordIdbs ? (
        <Text>{t('components:credential_card.portal_does_not_exist.label')}</Text>
      ) : serviceRecordAccountId === null ? (
        <Text>{t('components:credential_card.account_does_not_exist.label')}</Text>
      ) : (
        <Grid w={'100%'} data-test-id={`${credential?.portal?.id}-credential-information`}>
          <Flex direction="column" p={2} mb={2}>
            <Flex alignItems="center" mb={10} justifyContent={'space-between'} minHeight={75}>
              <Box minWidth={150}>
                <Text fontSize={'lg'} fontWeight={'bold'}>
                  {credential?.portal?.name}
                </Text>
              </Box>
              <Box flex={0.8} pl={5}>
                <StatusTag resourceKey={'account'} currentStatus={credential?.status ?? 0} width={'100%'} />
              </Box>
            </Flex>
            <Flex alignItems="center" wrap="wrap" justifyContent={'space-between'}>
              <Button
                flex={1}
                minWidth="min-content"
                mb={3}
                mr={2}
                rightIcon={<MdOpenInNew />}
                variant="outline"
                size="sm"
                data-test-id={`open-credential-button`}
                flexShrink={0}
              >
                <Link
                  // @ts-ignore
                  to={`/app/op/account/${account_id}`}
                  data-test-id={'edit-'}
                  color={'black'}
                >
                  {t('components:credential_card.account_details.label')}
                </Link>
              </Button>
              <Button
                flex={1}
                minWidth="min-content"
                mb={3}
                mr={1}
                rightIcon={<MdOpenInNew />}
                variant="primary"
                size="sm"
                data-test-id={`open-credential-button`}
                flexShrink={0}
                onClick={() => window.open(credential?.portal?.url, '_blank')}
              >
                {t('account:portal.open')}
              </Button>
            </Flex>
          </Flex>

          {(credential?.status === 200 && serviceRecordStatus !== 500) ||
          (credential?.status === 200 && serviceRecordStatus === 500 && serviceRecordStatusReason === 501) ? (
            <Grid mt={2} p={2}>
              <Stack spacing={4}>
                <Grid gap={2}>
                  <Stack direction={'row'} spacing={4} alignItems={'flex-end'}>
                    {simpleInput({
                      preventLastPassAutoFill: true,
                      name: 'username',
                      label: t('components:credential_card.username.label'),
                      register,
                      errors,
                      disabled: !canEdit,
                      noAddonSpacing: true,
                      rightAddon: (
                        <AddonIconButton
                          icon={value === credential?.username && showCheckIcon ? <CheckIcon /> : <IoIosCopy />}
                          aria-label={'copy credentials username button'}
                          onClick={() => {
                            setShowCheckIcon.on();
                            copy(credential?.username ?? '');
                            setTimeout(setShowCheckIcon.off, SHOW_CHECKICON_DURATION);
                          }}
                        />
                      ),
                    })}
                  </Stack>

                  <Grid gap={2}>
                    <Stack direction={'row'} spacing={4} alignItems={'flex-end'}>
                      {simpleInput({
                        preventLastPassAutoFill: true,
                        name: 'password',
                        label: t('components:credential_card.password.label'),
                        register,
                        errors: randomPassword ? null : errors,
                        disabled: !canEdit || randomPassword,
                        noAddonSpacing: true,
                        rightAddon: (
                          <AddonIconButton
                            icon={value === credential?.password && showCheckIcon ? <CheckIcon /> : <IoIosCopy />}
                            disabled={canEdit}
                            aria-label={'copy accounts password button'}
                            onClick={() => {
                              setShowCheckIcon.on();
                              copy(credential?.password ?? '');
                              setTimeout(setShowCheckIcon.off, SHOW_CHECKICON_DURATION);
                            }}
                          />
                        ),
                      })}
                    </Stack>
                    {canEdit &&
                      simpleCheckBox({
                        name: 'random_password',
                        label: t('components:credential_card.random_password.label'),
                        register,
                        errors,
                        disabled: !canEdit,
                      })}
                  </Grid>

                  {credential?.portal?.requires_totp && credential?.has_totp_secret && (
                    <TotpCodeDisplay account_id={account_id} hasTotpSecret={!!credential?.has_totp_secret} />
                  )}

                  <Grid w="full" templateColumns={'.5fr .5fr'} gap={4} rowGap={9} autoFlow={'dense'}>
                    <Button
                      variant="primary"
                      size="sm"
                      bg={canEdit ? 'red.500' : 'orange'}
                      _hover={{
                        bg: canEdit ? 'red.600' : 'orange.400',
                      }}
                      onClick={() => {
                        if (canEdit) {
                          reset({
                            status: credential?.status,
                            //@ts-ignore
                            portal_id: credential?.portal?.id,
                            workshop_id: credential?.workshop_id,
                            username: credential?.username,
                            password: credential?.password,
                            random_password: false,
                          });
                        }
                        setCanEdit(!canEdit);
                      }}
                    >
                      {canEdit ? t('common:abort') : t('common:edit')}
                    </Button>

                    {canEdit && (
                      <Button
                        type="submit"
                        variant="primary"
                        size="sm"
                        bg={'blue.500'}
                        _hover={{
                          bg: 'blue.600',
                        }}
                        data-test-id="credentials-submit-button"
                        disabled={(submitCount! > 0 && !isValid) || isLoading}
                      >
                        {t('common:submit')}
                      </Button>
                    )}
                  </Grid>
                </Grid>
              </Stack>
            </Grid>
          ) : credential?.status === 200 && serviceRecordStatus === 500 ? (
            <Text>{t('service_record:status.clarification.resolve_conversation.label')}</Text>
          ) : null}
        </Grid>
      )}
      {credential && (
        <Button
          mt={6}
          size={'sm'}
          color="red.600"
          bgColor={'red.100'}
          borderColor="red.600"
          variant="outline"
          _hover={{
            bg: 'red.600',
            color: 'white',
          }}
          _active={{
            bg: 'red.600',
            color: 'white',
          }}
          onClick={() => {
            NiceModal.show(AlertModal as any, {
              onSubmit: () => {
                updateAccountStatusMutation
                  .mutateAsync({
                    account_id: account_id,
                    status: 0,
                    username: credential?.username as string,
                    password: credential?.password,
                    random_password: false,
                  })
                  .then(() => {
                    return queryClient.invalidateQueries({ queryKey });
                  });
              },
              children: t('account:status.unregister.text'),
              content: {
                header: t('account:status.unregister.label'),
                footer: {
                  buttons: {
                    cancelCaption: t('common:cancel'),
                    actionCaption: t('account:status.unregister.label'),
                  },
                },
              },
              redSubmitBttn: true,
            });
          }}
        >
          {t('account:status.unregister.label')}
        </Button>
      )}
    </Grid>
  );
};

export default CredentialCard;
